import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { usePackages } from '../hooks/usePackages'
import {
  FiMapPin,
  FiUser,
  FiPhone,
  FiMail,
  FiX,
} from 'react-icons/fi'

const WHATSAPP_NUMBER = '7092210922'

export default function EnquiryPopup() {
  const location = useLocation()
  const { packages = [], loading } = usePackages()

  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')

  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  const [infants, setInfants] = useState(0)

  const [packageName, setPackageName] = useState('')
  const [packageQuery, setPackageQuery] = useState('')
  const [showPackageDropdown, setShowPackageDropdown] =
    useState(false)

  // Open popup after 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true)
    }, 2500)

    return () => clearTimeout(timer)
  }, [])

  // Detect package name from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/')

    if (
      (pathParts[1] === 'package' ||
        pathParts[1] === 'booking') &&
      pathParts[2]
    ) {
      let decodedSlug = pathParts[2]

      try {
        decodedSlug = decodeURIComponent(decodedSlug)
      } catch {
        // Keep original slug if decoding fails
      }

      const currentSlug = decodedSlug
        .replace(/-/g, ' ')
        .toLowerCase()
        .trim()

      const matchedPackage = packages.find((item) => {
        const itemName = String(item.name || '')
          .toLowerCase()
          .trim()

        const itemCity = String(item.city || '')
          .toLowerCase()
          .trim()

        return (
          itemName === currentSlug ||
          itemCity === currentSlug
        )
      })

      if (matchedPackage) {
        setPackageName(matchedPackage.name || '')
      } else {
        setPackageName(decodedSlug.replace(/-/g, ' '))
      }

      setPackageQuery('')
      setShowPackageDropdown(false)
    } else {
      setPackageName('')
      setPackageQuery('')
      setShowPackageDropdown(false)
    }
  }, [location.pathname, packages])

  // Filter packages from API data
  const filteredPackages = packages.filter((item) => {
    const searchText = packageQuery.trim().toLowerCase()

    if (!searchText) return false

    const itemName = String(item.name || '').toLowerCase()
    const itemCity = String(item.city || '').toLowerCase()

    return (
      itemName.includes(searchText) ||
      itemCity.includes(searchText)
    )
  })

  // Select a package
  const selectPackage = (item) => {
    setPackageName(item.name || item.city || '')
    setPackageQuery('')
    setShowPackageDropdown(false)
  }

  // Adult counter
  const increaseAdults = () =>
    setAdults((value) => value + 1)

  const decreaseAdults = () =>
    setAdults((value) => Math.max(1, value - 1))

  // Children counter
  const increaseChildren = () =>
    setChildren((value) => value + 1)

  const decreaseChildren = () =>
    setChildren((value) => Math.max(0, value - 1))

  // Infant counter
  const increaseInfants = () =>
    setInfants((value) => value + 1)

  const decreaseInfants = () =>
    setInfants((value) => Math.max(0, value - 1))

  // Submit enquiry to WhatsApp
  const handleSubmit = (e) => {
    e.preventDefault()

    const cleanMobile = mobile.replace(/\D/g, '')

    if (!/^[0-9]{10}$/.test(cleanMobile)) {
      alert('Please enter a valid 10-digit mobile number.')
      return
    }

    const message = `Hello DIRA HOLIDAYS! I have a new holiday enquiry.

Enquiry Details:
- Package: ${packageName}
- Name: ${name}
- Mobile: +91 ${cleanMobile}
- Email: ${email}
- Adults: ${adults}
- Children: ${children}
- Infants: ${infants}`

    const whatsappUrl =
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`

    window.open(
      whatsappUrl,
      '_blank',
      'noopener,noreferrer'
    )

    setIsOpen(false)
  }

  if (!isOpen) return null

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

        .enquiry-overlay,
        .enquiry-overlay * {
          box-sizing: border-box;
        }

        .enquiry-overlay {
          --dira-blue: #0909c9;
          --dira-yellow: #ffb000;
          --dira-light-blue: #e9efff;
          --dira-border: #d9deea;
          --dira-text: #111827;
          --dira-muted: #777777;

          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 15px;
          background: rgba(4, 4, 70, 0.62);
          font-family: 'Poppins', sans-serif;
          overflow-y: auto;
        }

        .enquiry-popup {
          width: 100%;
          max-width: 680px;
          max-height: calc(100vh - 30px);
          overflow-y: auto;
          background: #ffffff;
          border-radius: 18px;
          padding: 30px 32px;
          position: relative;
          border: none;
          box-shadow: 0 20px 60px rgba(0, 0, 80, 0.28);
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
        }

        .enquiry-popup input,
        .enquiry-popup button {
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
        }

        /* Close button */
        .close-button {
          position: absolute;
          top: 15px;
          right: 17px;
          width: 34px;
          height: 34px;
          min-width: 34px;
          min-height: 34px;
          padding: 0;
          border: 1px solid #dfe4f5;
          background: #f1f3ff;
          border-radius: 50%;
          color: var(--dira-blue);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 0;
          transition: background 0.2s ease, color 0.2s ease,
            transform 0.2s ease, border-color 0.2s ease;
        }

        .close-button svg {
          width: 18px;
          height: 18px;
          display: block;
          color: inherit;
          stroke-width: 2;
        }

        .close-button:hover {
          background: var(--dira-blue);
          border-color: var(--dira-blue);
          color: #ffffff;
          transform: rotate(90deg);
        }

        /* Main heading - no orange underline */
        .enquiry-heading {
          margin: 0 45px 22px 0;
          font-size: 27px;
          line-height: 1.45;
          font-weight: 500;
          color: var(--dira-blue);
          letter-spacing: 0;
        }

        .package-section {
          margin-bottom: 23px;
        }

        .package-label,
        .form-label {
          display: block;
          font-size: 14px;
          margin-bottom: 8px;
          color: var(--dira-text);
          font-weight: 500;
        }

        .package-input-wrapper {
          position: relative;
          width: 100%;
        }

        .package-input {
          width: 100%;
          height: 53px;
          border: 1px solid var(--dira-border);
          border-radius: 10px;
          padding: 0 48px 0 15px;
          font-size: 15px;
          color: var(--dira-text);
          background: #ffffff;
          outline: none;
          font-weight: 400;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .package-input:focus {
          border-color: var(--dira-blue);
          box-shadow: 0 0 0 3px rgba(9, 9, 201, 0.08);
        }

        .package-input::placeholder,
        .form-input::placeholder {
          color: #a7a7a7;
          opacity: 1;
        }

        /* Same navy blue for all icons */
        .package-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--dira-blue);
          pointer-events: none;
        }

        .input-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--dira-blue);
          pointer-events: none;
        }

        .package-dropdown {
          position: absolute;
          top: calc(100% + 5px);
          left: 0;
          right: 0;
          z-index: 100001;
          background: #ffffff;
          border: 1px solid var(--dira-border);
          border-radius: 10px;
          box-shadow: 0 8px 22px rgba(0, 0, 80, 0.12);
          max-height: 220px;
          overflow-y: auto;
        }

        .package-option {
          width: 100%;
          display: block;
          padding: 12px 15px;
          border: none;
          border-bottom: 1px solid #eeeeee;
          background: #ffffff;
          color: var(--dira-text);
          text-align: left;
          font-size: 14px;
          cursor: pointer;
          font-weight: 400;
          transition: background 0.2s ease;
        }

        .package-option:last-child {
          border-bottom: none;
        }

        .package-option:hover,
        .package-option:focus {
          background: #f1f3ff;
          outline: none;
        }

        .package-option-name {
          display: block;
          font-weight: 500;
        }

        .package-option-city {
          display: block;
          margin-top: 3px;
          color: var(--dira-muted);
          font-size: 12px;
          font-weight: 400;
        }

        .package-no-results {
          padding: 13px 15px;
          color: var(--dira-muted);
          font-size: 13px;
          font-weight: 400;
        }

        /* Personal Details - no straight line */
        .personal-heading-wrapper {
          display: flex;
          align-items: center;
          width: 100%;
          margin-bottom: 18px;
        }

        .personal-heading {
          margin: 0;
          font-size: 21px;
          font-weight: 500;
          color: var(--dira-text);
        }

        .name-mobile-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 17px;
        }

        .form-label {
          margin-bottom: 7px;
        }

        .input-icon-wrapper {
          position: relative;
          width: 100%;
        }

        .form-input {
          width: 100%;
          height: 53px;
          border: 1px solid var(--dira-border);
          border-radius: 10px;
          padding: 0 48px 0 14px;
          font-size: 15px;
          outline: none;
          background: #ffffff;
          color: var(--dira-text);
          font-weight: 400;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .form-input:focus {
          border-color: var(--dira-blue);
          box-shadow: 0 0 0 3px rgba(9, 9, 201, 0.08);
        }

        .mobile-wrapper {
          display: flex;
          width: 100%;
        }

        .country-code {
          height: 53px;
          width: 62px;
          flex-shrink: 0;
          border: 1px solid var(--dira-border);
          border-right: none;
          border-radius: 10px 0 0 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--dira-blue);
          font-size: 14px;
          font-weight: 500;
          background: #f8f9ff;
        }

        .mobile-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 0;
        }

        .mobile-input {
          padding-right: 48px;
          border-radius: 0 10px 10px 0;
        }

        .email-section {
          margin-bottom: 20px;
        }

        .counter-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
          margin-bottom: 22px;
        }

        .counter-wrapper {
          width: 100%;
          min-width: 0;
        }

        .counter-box {
          height: 50px;
          border: 1px solid var(--dira-border);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: space-around;
          background: #ffffff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .counter-box:hover {
          border-color: var(--dira-blue);
          box-shadow: 0 0 0 3px rgba(9, 9, 201, 0.05);
        }

        .counter-button {
          border: none;
          background: transparent;
          font-size: 23px;
          cursor: pointer;
          padding: 3px 9px;
          color: var(--dira-blue);
          line-height: 1;
          font-weight: 500;
          transition: color 0.2s ease, transform 0.2s ease;
        }

        .counter-button:hover {
          color: var(--dira-blue);
          transform: scale(1.08);
        }

        .counter-value {
          min-width: 25px;
          text-align: center;
          font-size: 18px;
          font-weight: 500;
          color: var(--dira-text);
        }

        .submit-button {
          width: 100%;
          height: 55px;
          border: none;
          border-radius: 30px;
          background: var(--dira-yellow);
          color: #111111;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease;
          box-shadow: 0 7px 18px rgba(255, 176, 0, 0.2);
        }

        .submit-button:hover {
          background: var(--dira-blue);
          color: #ffffff;
          box-shadow: 0 8px 20px rgba(9, 9, 201, 0.2);
        }

        @media (max-width: 700px) {
          .enquiry-popup {
            max-width: 95vw;
            padding: 27px 24px 25px;
          }

          .enquiry-heading {
            font-size: 24px;
          }

          .name-mobile-grid {
            gap: 15px;
          }

          .counter-grid {
            gap: 12px;
          }
        }

        @media (max-width: 520px) {
          .enquiry-overlay {
            padding: 10px;
          }

          .enquiry-popup {
            max-width: 100%;
            max-height: calc(100vh - 20px);
            border-radius: 15px;
            padding: 24px 18px 20px;
          }

          .enquiry-heading {
            font-size: 22px;
            margin-right: 40px;
            margin-bottom: 18px;
          }

          .close-button {
            top: 11px;
            right: 12px;
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
          }

          .close-button svg {
            width: 17px;
            height: 17px;
          }

          .package-section {
            margin-bottom: 20px;
          }

          .package-input {
            height: 50px;
            font-size: 15px;
          }

          .personal-heading {
            font-size: 20px;
          }

          .personal-heading-wrapper {
            margin-bottom: 16px;
          }

          .name-mobile-grid {
            grid-template-columns: 1fr;
            gap: 14px;
            margin-bottom: 14px;
          }

          .form-input {
            height: 50px;
          }

          .country-code {
            height: 50px;
            width: 58px;
          }

          .email-section {
            margin-bottom: 17px;
          }

          .counter-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            margin-bottom: 19px;
          }

          .counter-box {
            height: 48px;
          }

          .counter-button {
            font-size: 21px;
            padding: 3px 5px;
          }

          .counter-value {
            font-size: 17px;
          }

          .submit-button {
            height: 52px;
          }
        }

        @media (max-width: 380px) {
          .enquiry-popup {
            padding: 21px 15px 18px;
          }

          .enquiry-heading {
            font-size: 20px;
          }

          .personal-heading {
            font-size: 19px;
          }

          .counter-grid {
            grid-template-columns: 1fr;
          }

          .counter-box {
            height: 45px;
          }
        }
      `}</style>

      <div
        className="enquiry-overlay"
        onClick={() => setIsOpen(false)}
      >
        <div
          className="enquiry-popup"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="close-button"
            onClick={() => setIsOpen(false)}
            aria-label="Close enquiry form"
          >
            <FiX />
          </button>

          <h2 className="enquiry-heading">
            Want to Go For A Memorable Holiday?
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="package-section">
              <label
                className="package-label"
                htmlFor="enquiry-package"
              >
                Package Name
              </label>

              <div className="package-input-wrapper">
                <input
                  id="enquiry-package"
                  type="text"
                  value={packageName}
                  onChange={(e) => {
                    const value = e.target.value

                    setPackageName(value)
                    setPackageQuery(value)
                    setShowPackageDropdown(value.trim().length > 0)
                  }}
                  onFocus={() => {
                    if (packageName.trim()) {
                      setPackageQuery(packageName)
                      setShowPackageDropdown(true)
                    }
                  }}
                  onBlur={() => {
                    setShowPackageDropdown(false)
                  }}
                  placeholder="Search or select a package"
                  className="package-input"
                  autoComplete="off"
                  required
                />

                <FiMapPin
                  className="package-icon"
                  size={20}
                  strokeWidth={1.8}
                />

                {showPackageDropdown &&
                  packageQuery.trim() !== '' && (
                    <div className="package-dropdown">
                      {loading ? (
                        <div className="package-no-results">
                          Loading packages...
                        </div>
                      ) : filteredPackages.length > 0 ? (
                        filteredPackages.slice(0, 8).map((item, index) => (
                          <button
                            key={
                              item._id ||
                              item.id ||
                              `${item.name || item.city}-${index}`
                            }
                            type="button"
                            className="package-option"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectPackage(item)}
                          >
                            <span className="package-option-name">
                              {item.name || item.city || 'Unnamed Package'}
                            </span>

                            {item.city && (
                              <span className="package-option-city">
                                {item.city}
                              </span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="package-no-results">
                          No matching packages found
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>

            <div className="personal-heading-wrapper">
              <h3 className="personal-heading">
                Personal Details
              </h3>
            </div>

            <div className="name-mobile-grid">
              <div>
                <label
                  className="form-label"
                  htmlFor="enquiry-name"
                >
                  Name
                </label>

                <div className="input-icon-wrapper">
                  <input
                    id="enquiry-name"
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="form-input"
                    autoComplete="name"
                  />

                  <FiUser
                    className="input-icon"
                    size={21}
                    strokeWidth={1.8}
                  />
                </div>
              </div>

              <div>
                <label
                  className="form-label"
                  htmlFor="enquiry-mobile"
                >
                  Mobile No.
                </label>

                <div className="mobile-wrapper">
                  <div className="country-code">+91</div>

                  <div className="mobile-input-wrapper">
                    <input
                      id="enquiry-mobile"
                      type="tel"
                      placeholder="Mobile No."
                      value={mobile}
                      onChange={(e) => {
                        setMobile(
                          e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 10)
                        )
                      }}
                      required
                      className="form-input mobile-input"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      title="Enter a 10-digit mobile number"
                      autoComplete="tel-national"
                    />

                    <FiPhone
                      className="input-icon"
                      size={21}
                      strokeWidth={1.8}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="email-section">
              <label
                className="form-label"
                htmlFor="enquiry-email"
              >
                Email ID
              </label>

              <div className="input-icon-wrapper">
                <input
                  id="enquiry-email"
                  type="email"
                  placeholder="Your E-Mail Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                  autoComplete="email"
                />

                <FiMail
                  className="input-icon"
                  size={21}
                  strokeWidth={1.8}
                />
              </div>
            </div>

            <div className="counter-grid">
              <Counter
                label="Adult"
                value={adults}
                decrease={decreaseAdults}
                increase={increaseAdults}
              />

              <Counter
                label="Child"
                value={children}
                decrease={decreaseChildren}
                increase={increaseChildren}
              />

              <Counter
                label="Infant"
                value={infants}
                decrease={decreaseInfants}
                increase={increaseInfants}
              />
            </div>

            <button type="submit" className="submit-button">
              Send an Enquiry
            </button>
          </form>
        </div>
      </div>
    </>
  )
}

function Counter({ label, value, decrease, increase }) {
  return (
    <div className="counter-wrapper">
      <label className="form-label">{label}</label>

      <div className="counter-box">
        <button
          type="button"
          onClick={decrease}
          className="counter-button"
          aria-label={`Decrease ${label}`}
        >
          −
        </button>

        <span className="counter-value">{value}</span>

        <button
          type="button"
          onClick={increase}
          className="counter-button"
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  )
}